<script>
import AutomationActionTeamMessageInput from './AutomationActionTeamMessageInput.vue';
import AutomationActionFileInput from './AutomationFileInput.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

const CONTACT_EMAIL_TOKEN = '{{contact.email}}';

export default {
  components: {
    AutomationActionTeamMessageInput,
    AutomationActionFileInput,
    WootMessageEditor,
    NextButton,
    AdaptiveSelect,
    NextInput,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => null,
    },
    actionTypes: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    errorMessage: {
      type: String,
      default: '',
    },
    showActionInput: {
      type: Boolean,
      default: true,
    },
    initialFileName: {
      type: String,
      default: '',
    },
    isMacro: {
      type: Boolean,
      default: false,
    },
    dropdownMaxHeight: {
      type: String,
      default: 'max-h-80',
    },
  },
  emits: ['update:modelValue', 'input', 'removeAction', 'resetAction'],
  computed: {
    action_name: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_name;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_name: value });
        this.$emit('input', { ...payload, action_name: value });
      },
    },
    action_params: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_params;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_params: value });
        this.$emit('input', { ...payload, action_params: value });
      },
    },
    inputType() {
      return this.actionTypes.find(action => action.key === this.action_name)
        .inputType;
    },
    actionNameAsSelectModel: {
      get() {
        if (!this.action_name) return null;
        const found = this.actionTypes.find(a => a.key === this.action_name);
        return found ? { id: found.key, name: found.label } : null;
      },
      set(value) {
        this.action_name = value?.id || value;
      },
    },
    actionTypesAsOptions() {
      return this.actionTypes.map(a => ({
        id: a.key,
        name: a.label,
        icon: a.icon,
      }));
    },
    isVerticalLayout() {
      return ['team_message', 'textarea', 'email'].includes(this.inputType);
    },
    castMessageVmodel: {
      get() {
        if (Array.isArray(this.action_params)) {
          return this.action_params[0];
        }
        return this.action_params;
      },
      set(value) {
        this.action_params = value;
      },
    },
    selectedPipelineId: {
      get() {
        if (
          Array.isArray(this.action_params) &&
          this.action_params.length > 1
        ) {
          return this.action_params[1];
        }
        return null;
      },
      set(val) {
        const stageId = this.selectedStageId;
        this.action_params = stageId ? [stageId, val] : [null, val];
      },
    },
    selectedStageId: {
      get() {
        if (
          Array.isArray(this.action_params) &&
          this.action_params.length > 0
        ) {
          return this.action_params[0];
        }
        return null;
      },
      set(val) {
        this.action_params = [val, this.selectedPipelineId];
      },
    },
    pipelinesAsOptions() {
      const pipelines = this.$store.getters['kanban/getActivePipelines'] || [];
      return pipelines.map(p => ({ id: p.id, name: p.name }));
    },
    stagesAsOptions() {
      if (!this.selectedPipelineId) return [];
      const stages =
        this.$store.getters['kanban/getStagesByPipeline'](
          this.selectedPipelineId
        ) || [];
      return stages.map(s => ({ id: s.id, name: s.name }));
    },
    pipelineSelectModel() {
      if (!this.selectedPipelineId) return null;
      const pipelines = this.$store.getters['kanban/getActivePipelines'] || [];
      const found = pipelines.find(p => p.id === this.selectedPipelineId);
      return found
        ? { id: found.id, name: found.name }
        : { id: this.selectedPipelineId, name: this.selectedPipelineId };
    },
    stageSelectModel() {
      if (!this.selectedStageId) return null;
      const stages =
        this.$store.getters['kanban/getStagesByPipeline'](
          this.selectedPipelineId
        ) || [];
      const found = stages.find(s => s.id === this.selectedStageId);
      return found
        ? { id: found.id, name: found.name }
        : { id: this.selectedStageId, name: this.selectedStageId };
    },
  },
  watch: {
    action_name(newVal) {
      if (newVal === 'add_to_pipeline') {
        this.loadPipelines();
      }
    },
  },
  mounted() {
    if (this.action_name === 'add_to_pipeline') {
      this.loadPipelines();
    }
  },
  methods: {
    removeAction() {
      this.$emit('removeAction');
    },
    resetAction() {
      this.$emit('resetAction');
    },
    onActionNameChange(value) {
      this.actionNameAsSelectModel = value;
      this.resetAction();
    },
    async loadPipelines() {
      try {
        await this.$store.dispatch('kanban/fetchPipelines');
        const pipelineId = this.selectedPipelineId;
        if (pipelineId) {
          await this.$store.dispatch('kanban/fetchStages', pipelineId);
        }
      } catch (e) {
        // Silently fail
      }
    },
    onPipelineChange(value) {
      const pipelineId = value?.id || value;
      this.selectedPipelineId = pipelineId;
      this.selectedStageId = null;
      this.action_params = [null, pipelineId];
      if (pipelineId) {
        this.$store.dispatch('kanban/fetchStages', pipelineId);
      }
    },
    onStageChange(value) {
      const stageId = value?.id || value;
      this.selectedStageId = stageId;
      this.action_params = stageId
        ? [stageId, this.selectedPipelineId]
        : [null, this.selectedPipelineId];
    },

    insertContactEmailToken() {
      const existingEmails = (this.castMessageVmodel || '')
        .split(',')
        .map(email => email.trim())
        .filter(Boolean);

      const hasContactEmail = existingEmails.some(
        email => email.replace(/\s+/g, '') === CONTACT_EMAIL_TOKEN
      );
      if (hasContactEmail) return;

      this.action_params = [[...existingEmails, CONTACT_EMAIL_TOKEN].join(',')];
    },
  },
};
</script>

<template>
  <li class="list-none py-2 first:pt-0 last:pb-0">
    <div
      class="flex flex-col gap-2"
      :class="{ 'animate-wiggle': errorMessage }"
    >
      <div class="flex items-center gap-2">
        <AdaptiveSelect
          presentation="compact"
          :model-value="actionNameAsSelectModel"
          :options="actionTypesAsOptions"
          :dropdown-max-height="dropdownMaxHeight"
          disable-deselect
          class="flex-shrink-0"
          @update:model-value="onActionNameChange"
        />
        <template v-if="showActionInput && !isVerticalLayout">
          <AdaptiveSelect
            v-if="inputType === 'search_select'"
            v-model="action_params"
            presentation="compact"
            :options="dropdownValues"
            :dropdown-max-height="dropdownMaxHeight"
          />
          <AdaptiveSelect
            v-else-if="inputType === 'multi_select'"
            v-model="action_params"
            mode="multiple"
            presentation="compact"
            :options="dropdownValues"
            :dropdown-max-height="dropdownMaxHeight"
          />
          <NextInput
            v-else-if="inputType === 'url'"
            v-model="action_params"
            type="url"
            size="sm"
            :placeholder="$t('AUTOMATION.ACTION.URL_INPUT_PLACEHOLDER')"
          />
          <AutomationActionFileInput
            v-else-if="inputType === 'attachment'"
            v-model="action_params"
            :initial-file-name="initialFileName"
          />
          <template v-else-if="inputType === 'kanban_select'">
            <AdaptiveSelect
              presentation="compact"
              :model-value="pipelineSelectModel"
              :options="pipelinesAsOptions"
              :placeholder="$t('AUTOMATION.ACTION.SELECT_PIPELINE_PLACEHOLDER')"
              disable-deselect
              :dropdown-max-height="dropdownMaxHeight"
              @update:model-value="onPipelineChange"
            />
            <AdaptiveSelect
              presentation="compact"
              :model-value="stageSelectModel"
              :options="stagesAsOptions"
              :placeholder="$t('AUTOMATION.ACTION.SELECT_STAGE_PLACEHOLDER')"
              disable-deselect
              :disabled="!selectedPipelineId"
              :dropdown-max-height="dropdownMaxHeight"
              @update:model-value="onStageChange"
            />
          </template>
        </template>
        <NextButton
          v-if="!isMacro"
          sm
          solid
          slate
          icon="i-lucide-trash"
          class="flex-shrink-0"
          @click="removeAction"
        />
      </div>
      <div v-if="inputType === 'email'" class="flex items-center w-full gap-2">
        <NextInput
          v-model="action_params"
          type="text"
          size="sm"
          class="flex-1"
          :placeholder="$t('AUTOMATION.ACTION.EMAIL_INPUT_PLACEHOLDER')"
        />
        <NextButton
          sm
          faded
          slate
          class="flex-shrink-0 whitespace-nowrap"
          :label="$t('AUTOMATION.ACTION.INSERT_CONTACT_EMAIL')"
          @click="insertContactEmailToken"
        />
      </div>
      <AutomationActionTeamMessageInput
        v-else-if="inputType === 'team_message'"
        v-model="action_params"
        :teams="dropdownValues"
        :dropdown-max-height="dropdownMaxHeight"
      />
      <WootMessageEditor
        v-else-if="inputType === 'textarea'"
        v-model="castMessageVmodel"
        rows="4"
        enable-variables
        :placeholder="$t('AUTOMATION.ACTION.TEAM_MESSAGE_INPUT_PLACEHOLDER')"
        class="[&_.ProseMirror-menubar]:hidden px-3 py-1 bg-n-alpha-1 rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong"
      />
    </div>
    <span v-if="errorMessage" class="text-sm text-n-ruby-11">
      {{ errorMessage }}
    </span>
  </li>
</template>
